'use client'

import { clearActiveUser, setActiveUser } from '@hermes/api-sdk'
import { useLocale } from '@hooks/useLocale'
import { useAuthStore } from '@lib/stores/auth'
import keyWithCookieSuffix from '@lib/utils/keyWithCookieSuffix'
import { deleteCookie, getCookie, setCookie } from 'cookies-next'
import { useCallback, useEffect } from 'react'

// The goal of this hook is now to bridge our legacy vue and next.js web.
// Without legacy we could store all tokens in our zustand store
const useUserToken = () => {
  const locale = useLocale()
  const cookieName = keyWithCookieSuffix('token', locale)
  const refreshCookieName = keyWithCookieSuffix('refreshToken', locale)
  const accessJWT = useAuthStore((state) => state.accessJWT)
  const setAccessJWT = useAuthStore((state) => state.setAccessJWT)
  const refreshJWT = useAuthStore((state) => state.refreshJWT)
  const setRefreshJWT = useAuthStore((state) => state.setRefreshJWT)

  const deleteAllTokens = useCallback(() => {
    deleteCookie(cookieName)
    deleteCookie(refreshCookieName)
    setAccessJWT('')
    setRefreshJWT('')
    clearActiveUser()
  }, [cookieName, refreshCookieName, setAccessJWT, setRefreshJWT])

  const setToken = useCallback(
    (token: string) => {
      setActiveUser({
        token
      })
      setAccessJWT(token)
      setCookie(cookieName, token)
    },
    [cookieName, setAccessJWT]
  )

  const setRefreshToken = useCallback(
    (token: string) => {
      setRefreshJWT(token)
      setCookie(refreshCookieName, token)
    },
    [refreshCookieName, setRefreshJWT]
  )

  useEffect(() => {
    if (!accessJWT) {
      const browserToken = getCookie(cookieName)
      if (browserToken) {
        setToken(String(browserToken))
      }
    }

    if (!refreshJWT) {
      const browserRefreshToken = getCookie(refreshCookieName)
      if (browserRefreshToken) {
        setRefreshToken(String(browserRefreshToken))
      }
    }
  }, [accessJWT, refreshJWT, cookieName, refreshCookieName, setAccessJWT, setRefreshJWT])

  return {
    token: accessJWT || undefined,
    refreshToken: refreshJWT || undefined,
    deleteAllTokens,
    setToken,
    setRefreshToken
  }
}

export { useUserToken }
